import {
  ImageAddAction,
  ImageSetContentAction,
  KalturaAsset,
  KalturaAssetArgs,
  KalturaBooleanValue,
  KalturaCategoryItem,
  KalturaCategoryItemArgs,
  KalturaDoubleValue,
  KalturaImage,
  KalturaImageObjectType,
  KalturaLongValue,
  KalturaMetaDataType,
  KalturaMultilingualStringValue,
  KalturaMultilingualStringValueArray,
  KalturaStringValue,
  KalturaTranslationToken,
  KalturaUnifiedChannel,
  KalturaUrlResource,
} from '@kaltura-ott/ott-client-types/api/types';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, delay, map, mergeMap, retryWhen, tap } from 'rxjs/operators';

import { randString } from '../../fortests/utils';
import { KuxKalturaCategoryItemArgs } from '../../interfaces';
import { getAssetByCategoryId } from '../asset';
import { kalturaClient } from '../kaltura-clent';

import { updateAsset } from './asset';
import { addChildCategoryToParent, createRootCategory } from './category';
import { getCategoryStructsList } from './category-template';
import {
  BASE_KUX_METAS,
  CATEGORY_ID,
  CONTENT_KUX_METAS,
  InitialMetaConst,
  STYLING_KUX_METAS,
  WIDGET_KUX_METAS,
} from './config/metas';
import { ParentingMap, StructsMap } from './import-tree-v2';

const addImagesToCategory = (categoryId: number, images: Array<KalturaImage>): Observable<void> => {
  const client = kalturaClient();
  const actions: Observable<KalturaImage>[] = [];
  images.forEach((image) => {
    const imageAddAction = new ImageAddAction({
      image: new KalturaImage({
        imageTypeId: image.imageTypeId,
        imageObjectId: categoryId,
        imageObjectType: KalturaImageObjectType.category,
      }),
    });
    actions.push(client.request(imageAddAction));
  });
  return forkJoin(actions).pipe(
    mergeMap((responses: Array<KalturaImage>) => {
      const setContentActions: Observable<void>[] = [];
      responses.forEach((image, index) => {
        const setContentAction = new ImageSetContentAction({
          id: image.id,
          content: new KalturaUrlResource({ url: images[index].url }),
        });
        setContentActions.push(client.request(setContentAction));
      });
      return forkJoin(setContentActions);
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};

const createCategoryAndAsset = (
  category: KalturaCategoryItemArgs,
  asset: KalturaAssetArgs,
  images: Array<KalturaImage>,
): Observable<{ asset: KalturaAsset; category: KalturaCategoryItem }> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let createdCategory: any;
  return createRootCategory(category).pipe(
    // Todo <Tanya> BEO-8847
    delay(2000),
    mergeMap((category) => {
      if (images) {
        return addImagesToCategory(category.id, images).pipe(map(() => category));
      } else {
        return of(category);
      }
    }),
    mergeMap((category) => {
      createdCategory = category;
      return getAssetByCategoryId(createdCategory.id).pipe(
        map((asset) => {
          if (asset) {
            return asset;
          }
          throw new Error('Asset Not Ready Yet');
        }),
        // Todo <Tanya> BEO-8847
        retryWhen((errors) => {
          return errors.pipe(
            delay(2000),
            tap(() => console.log('retrying...', createdCategory.name)),
          );
        }),
      );
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mergeMap((assetToUpdate: any) => {
      delete assetToUpdate.type;
      delete assetToUpdate.externalId;
      // @ts-expect-error TODO Fix
      delete asset.metas.CategoryId;
      assetToUpdate.metas = asset.metas;
      assetToUpdate.tags = asset.tags;
      return updateAsset(assetToUpdate.id, assetToUpdate).pipe(
        map((createdAsset) => ({ asset: createdAsset, category: createdCategory })),
      );
    }),
  );
};

const ALLBASICMETAS = {
  ...CATEGORY_ID,
  ...BASE_KUX_METAS,
  ...STYLING_KUX_METAS,
  ...CONTENT_KUX_METAS,
  ...WIDGET_KUX_METAS,
};

interface MetaConfig {
  [key: string]: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMetaType = (systemName: string, value: any, metaConfig: MetaConfig = {}) => {
  const meta = (ALLBASICMETAS[systemName] || metaConfig[systemName]) as InitialMetaConst;
  if (meta) {
    switch (meta.dataType) {
      case KalturaMetaDataType.boolean:
        return new KalturaBooleanValue({ value });
      case KalturaMetaDataType.date:
        return new KalturaLongValue({ value });
      case KalturaMetaDataType.string:
        return new KalturaStringValue({ value });
      case KalturaMetaDataType.number:
        return new KalturaDoubleValue({ value });
      case KalturaMetaDataType.multilingualString:
        return new KalturaDoubleValue({ value });
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const assetJsonIntoObject = (jsonAsset: any, language: any): KalturaAssetArgs => {
  const asset: KalturaAssetArgs = {
    multilingualName: [new KalturaTranslationToken({ value: jsonAsset.name, language: language })],
    externalId: randString(16),
    metas: {},
    tags: {},
  };

  Object.keys(jsonAsset.metas).forEach((systemName) => {
    const meta = ALLBASICMETAS[systemName];
    const value = jsonAsset.metas[systemName].value;
    if (meta) {
      switch (meta.dataType) {
        case KalturaMetaDataType.boolean:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaBooleanValue({ value });
          break;
        case KalturaMetaDataType.date:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaLongValue({ value });
          break;
        case KalturaMetaDataType.string:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaStringValue({ value });
          break;
        case KalturaMetaDataType.number:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaDoubleValue({ value });
          break;
        case KalturaMetaDataType.multilingualString:
          // @ts-expect-error TODO Fix
          asset.metas[systemName] = new KalturaDoubleValue({ value });
          break;
      }
    }
  });

  Object.keys(jsonAsset.tags).forEach((systemName) => {
    // @ts-expect-error TODO Fix
    asset.tags[systemName] = new KalturaMultilingualStringValueArray();
    // @ts-expect-error TODO Fix
    asset.tags[systemName].objects = jsonAsset.tags[systemName].objects.map((ob: any) => {
      return new KalturaMultilingualStringValue({
        multilingualValue: [
          new KalturaTranslationToken({
            value: ob.value,
            language: language,
          }),
        ],
      });
    });
  });
  return asset;
};

const parentingMap: ParentingMap = {};

const doImport = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  JSONtree: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  language: any,
  withCollections = false,
  withImages = false,
  withDynamicData = false,
) => {
  return getCategoryStructsList().pipe(
    map((resp) => {
      const structsMap: StructsMap = {};
      resp.objects.forEach((struct) => {
        structsMap[struct.systemName] = struct.id;
      });
      return structsMap;
    }),
    catchError((err) => {
      return of(err);
    }),
    mergeMap((structsMap) => {
      const category: KuxKalturaCategoryItemArgs = {
        type: JSONtree.typeSystemName,
        multilingualName: [new KalturaTranslationToken({ value: JSONtree.name, language: language })],
      };
      if (withCollections && JSONtree.unifiedChannels?.length > 0) {
        category.unifiedChannels = JSONtree.unifiedChannels.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ch: any) =>
            new KalturaUnifiedChannel({
              id: ch.id,
              type: ch.type,
            }),
        );
      }

      if (withDynamicData && JSONtree.dynamicData) {
        Object.keys(JSONtree.dynamicData).forEach((key) => {
          if (!category.dynamicData) {
            category.dynamicData = {};
          }
          category.dynamicData[key] = new KalturaStringValue({ value: JSONtree.dynamicData[key].value });
        });
      }

      const asset = assetJsonIntoObject(JSONtree.asset, language);
      asset.type = structsMap[JSONtree.type];
      const categoryImages = withImages && JSONtree.images?.length > 0 ? JSONtree.images : null;
      return createCategoryAndAsset(category, asset, categoryImages).pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(({ asset, category }) => {
          parentingMap[JSONtree.id] = category.id;
        }),
      );
    }),
    mergeMap(({ asset, category }) => {
      if (JSONtree.oldParentId && parentingMap[JSONtree.oldParentId]) {
        console.log(`update parent -> ${parentingMap[JSONtree.oldParentId]}`);
        return addChildCategoryToParent({ childId: category.id, parentId: parentingMap[JSONtree.oldParentId] });
      }
      return of({ asset, category });
    }),
    catchError((err) => {
      return of(err);
    }),
  );
};

export const importTree = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  JSONtree: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forkRequests: Observable<any>[] = [],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  language: any,
  withCollections = false,
  withImages = false,
  withDynamicData = false,
) => {
  console.log('MAYPE ==> ', JSONtree.CREATED_ID);
  if (!forkRequests.length) {
    forkRequests.push(doImport(JSONtree, language, withCollections, withImages, withDynamicData));
  }

  if (JSONtree.children.length == 0) {
    return forkRequests;
  }

  if (JSONtree.images)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    JSONtree.children.forEach((subJsonTree: any) => {
      forkRequests.push(doImport(subJsonTree, language, withCollections, withImages, withDynamicData));
      importTree(subJsonTree, forkRequests, language, withCollections, withImages, withDynamicData);
    });
  console.log(forkRequests);
  return forkRequests;
};
